/**
 * Tracks whether the current user needs to (re-)accept the Terms of Service and Privacy Policy.
 *
 * Drives the in-app blocking dialog mounted at the App root. Fires whenever a user has
 * completed onboarding but their recorded acceptance versions don't match the current
 * domain-wide constants (i.e., we've revised the policies).
 */

import type { AppFlowsContainer } from '#flows.js';
import type { SessionState } from './SessionState.js';
import type { UserProfileState } from './UserProfileState.js';

import {
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
  requiresPolicyReacceptance,
} from '@elara/domain/policy-versions';

import { createState } from '#lib/signals-utils.js';

export function createPolicyAcceptanceState(
  sessionState: SessionState,
  userProfileState: UserProfileState,
  flows: AppFlowsContainer,
) {
  return createState({
    isAccepting: false,
    error: undefined as string | undefined,

    /**
     * True iff the user has finished onboarding but their recorded policy-acceptance
     * versions are stale (or absent). The `onboardingCompletedAt` gate prevents this from
     * interrupting the signup -> onboarding flow, where acceptance is captured via the
     * signup form checkbox.
     */
    get requiresAcceptance(): boolean {
      if (!sessionState.isAuthenticated || sessionState.isLoading) return false;
      if (userProfileState.isLoading) return false;
      const profile = userProfileState.profile;
      if (!profile) return false;
      return requiresPolicyReacceptance(profile);
    },

    async accept(): Promise<boolean> {
      this.isAccepting = true;
      this.error = undefined;
      try {
        await flows.acceptPolicies({
          termsVersion: CURRENT_TERMS_VERSION,
          privacyVersion: CURRENT_PRIVACY_VERSION,
        });
        return true;
      } catch (err) {
        console.error('[PolicyAcceptanceState] Failed to accept policies', { error: err });
        this.error = 'We couldn’t record your acceptance. Please try again.';
        return false;
      } finally {
        this.isAccepting = false;
      }
    },
  });
}

export type PolicyAcceptanceState = ReturnType<typeof createPolicyAcceptanceState>;
